import React, { ChangeEventHandler, FormEventHandler, ReactElement, useState } from 'react';

import Input from 'atomic/input/input';
import Button from 'atomic/button/button';
import { useFetch } from 'hooks/use-fetch';
import { useProductReviews } from 'hooks/use-product-reviews';
import { ReviewItemMessageInterface } from 'components/review/review.interface';

import './review-add-comment.scss';
import Textarea from 'atomic/textarea/textarea';
import Link from 'atomic/link/link';

interface ReviewAddCommentProps {
    reviewId: number;
}

const ReviewAddComment: React.FC<ReviewAddCommentProps> = ({ reviewId }): ReactElement => {
    const [showForm, setShowForm] = useState(false);
    const [content, setContent] = useState('');
    const [author, setAuthor] = useState('');

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSent, setIsSent] = useState<boolean>(false);

    const { addComment } = useProductReviews();

    const handleOnChangeContent: ChangeEventHandler<HTMLTextAreaElement> = e => {
        setContent(e.target.value);
        setErrors(prev => ({
            ...prev,
            content: '',
            global: ''
        }));
        setIsSent(false);
    };

    const handleOnChangeAuthor: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = e => {
        setAuthor(e.target.value);
        setErrors(prev => ({
            ...prev,
            author: '',
            global: ''
        }));
        setIsSent(false);
    };

    const { post, loading } = useFetch(`/product-review/comment/${reviewId}`);

    const handleOnSubmit: FormEventHandler<HTMLFormElement> = e => {
        e.preventDefault();

        post({
            productReviewId: reviewId,
            author,
            content
        }).then((result: {data: ReviewItemMessageInterface | null; errors?: Record<string, string>;}) => {
            if (result.errors) {
                setErrors(result.errors);
            } else {
                setContent('');
                setAuthor('');
                setErrors({});
                setIsSent(true);

                if (result.data) {
                    addComment({
                        productReviewId: reviewId,
                        comment: {
                            id: Number(result.data.id),
                            author: result.data.author,
                            content: result.data.content,
                            createdAt: new Date(),
                            type: 'user'
                        }
                    });
                }
            }
        });
    };

    const handleClickShowForm: React.MouseEventHandler<HTMLAnchorElement> = e => {
        e.preventDefault();

        setShowForm(true);
    };

    if (!showForm) {
        return (
            <div className="review-add-comment__show-form">
                <Link href="#" className="review-add-comment__show-form-button" onClick={handleClickShowForm}>
                    + Dodaj komentarz
                </Link>
            </div>
        );
    }

    return (
        <form className="review-add-comment" onSubmit={handleOnSubmit}>
            <div className="review-add-comment__row">
                <Textarea
                    label="Treść komentarza"
                    value={content}
                    name="content"
                    error={errors.content}
                    disabled={loading}
                    onChange={handleOnChangeContent}
                />
            </div>
            <div className="review-add-comment__row">
                <Input
                    className="review-add-comment__input"
                    type="input"
                    label="Autor"
                    value={author}
                    name="author"
                    error={errors.author}
                    disabled={loading}
                    onChange={handleOnChangeAuthor}
                />
                <Button type="submit" className="review-add-comment__button" disabled={loading}>
                    Dodaj komentarz
                </Button>
            </div>
            {errors.global && (
                <div className="review-add-comment__row">
                    <div className="review-add-comment__error">
                        {errors.global}
                    </div>
                </div>
            )}
            {isSent && (
                <div className="review-add-comment__row">
                    <div className="review-add-comment__sent">
                        Komentarz został wysłany.
                    </div>
                </div>
            )}
        </form>
    );
};

export default ReviewAddComment;
